import React, {
  ComponentType,
  useState
} from 'react'
import { Accordion } from 'semantic-ui-react'
// Local
import { CONDITION } from '../../../constant'
import { withAddressedConditions } from '../../common/conditionUtils'
import ClinicalAdminCondition from './clinicalAdminCondition'
import ClinicalAdminConditionHccHeaderTitle from './clinicalAdminConditionHccHeaderTitle'
import {
  EncounterConditionDisplay
} from '../../authenticated/types'
import { ObjectValues } from '../../../types/types'

const SafeAccordion = Accordion as unknown as ComponentType<any>

type ClinicalAdminConditionHccGroupProps = {
  mode?: string | null
  hccCode: string | null
  hccDescription: string | null
  activeConditionId: string | null
  conditionGroupTitle: string
  conditions: EncounterConditionDisplay[]
  onConditionAction: (
    action: ObjectValues<typeof CONDITION.ACTION>,
    condition: EncounterConditionDisplay | string | null
  ) => void
}

/**
 * Provides a component that displays conditions grouped under an HCC for the
 * clinical admin user role
 *
 * @param mode View mode. Options: "smart", null. Default: null
 * @param hccCode HCC code
 * @param hccDescription HCC description
 * @param activeConditionId currently set active conditionId from the
 * last call of "this.props.onConditionAction(ACTIVATE || DEACTIVATE, this.domKey)"
 *                          this value is set by: `${this.props.conditionGroupTitle}-${this.props.hccCode}`
 * @param conditionGroupTitle the conditionStatus group, values: KNOWN, POTENTIAL, or ACTIVITY
 * @param conditions List of conditions to display in the group
 * @param onConditionAction Callback for condition action.
 *        Arguments: action, condition
 */
function ClinicalAdminConditionHccGroup(
  props: ClinicalAdminConditionHccGroupProps
): JSX.Element | null {
  const {
    mode: modeProp,
    hccCode,
    hccDescription,
    activeConditionId: activeConditionIdProp,
    conditionGroupTitle,
    conditions: conditionsProp,
    onConditionAction
  } = props

  const domKey = `${conditionGroupTitle}-${hccCode}`
  const [ isActive, setIsActive ] = useState<boolean>(
    activeConditionIdProp === domKey
  )

  const handleClickTitle = () => {
    const {ACTIVATE} = CONDITION.ACTION
    const {DEACTIVATE} = CONDITION.ACTION
    const {READ} = CONDITION.ACTION

    if (isActive === true) {
      setIsActive(false)
      onConditionAction(DEACTIVATE, domKey)
    } else {
      setIsActive(true)
      onConditionAction(ACTIVATE, domKey)
      conditionsProp.forEach(c => {
        onConditionAction(READ, c)
      })
    }
  }

  const hccGroupTitle = (conditions: EncounterConditionDisplay[]): JSX.Element => (
    <ClinicalAdminConditionHccHeaderTitle
      conditions={ conditions }
      active={ isActive }
      hccCode={ hccCode || '' }
      hccDescription={ hccDescription || '' }
    />
  )

  const renderConditions = (
    conditions: EncounterConditionDisplay[]
  ): JSX.Element => {
    const isHccAddressed = withAddressedConditions(conditions).length > 0

    return (
      <div>
        <Accordion.Title
          key={ domKey }
          active={ isActive }
          onClick={ handleClickTitle }
        >
          <div>{ hccGroupTitle(conditions) }</div>
        </Accordion.Title>
        <Accordion.Content active={ isActive }>
          <SafeAccordion fluid>
            { conditionsProp.map((condition, displayIndex) => (
              <ClinicalAdminCondition
                key={ condition.id }
                mode={ modeProp }
                active={ isActive }
                hccChild={ true }
                hccAddressed={ isHccAddressed }
                condition={ condition }
                displayIndex={ displayIndex }
                onConditionAction={ onConditionAction }
              />
            )) }
          </SafeAccordion>
        </Accordion.Content>
      </div>
    )
  }

  return conditionsProp && conditionsProp.length > 0
    ? renderConditions(conditionsProp)
    : null
}

export default ClinicalAdminConditionHccGroup
